import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, RefreshCw, CheckCircle2, Send, FileText, AlertCircle, Download, Award, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  draft:      "bg-muted text-muted-foreground border-border",
  approved:   "bg-blue-500/10 text-blue-600 border-blue-500/20",
  processing: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  paid:       "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  partial:    "bg-orange-500/10 text-orange-600 border-orange-500/20",
  failed:     "bg-red-500/10 text-red-600 border-red-500/20",
};

// Current FY helper
function currentFY() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 3 ? `${y}-${String(y+1).slice(-2)}` : `${y-1}-${String(y).slice(-2)}`;
}
function currentQ() {
  const m = new Date().getMonth() + 1;
  if (m >= 4 && m <= 6)  return 1;
  if (m >= 7 && m <= 9)  return 2;
  if (m >= 10 && m <= 12) return 3;
  return 4;
}

function StatementLines({ stmtId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/sm/statements/${stmtId}/lines`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => toast.error("Failed to load lines"));
  }, [stmtId]);

  if (!data) return <div className="p-6 animate-pulse text-muted-foreground text-sm">Loading…</div>;
  const { statement: s, lines } = data;
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-semibold">{s.period_label} Statement</div>
          <div className="text-[12px] text-muted-foreground">{lines.length} partners · {fmt(s.total_net)} net payable</div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-[12px]">Close</button>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-[12px] min-w-[640px]">
        <thead className="border-b border-border bg-muted/20">
          <tr>{["Partner", "Level", "Entries", "Gross", "TDS", "Net Payable", "Bank", "Status"].map(h => (
            <th key={h} className="px-3 py-2.5 text-left text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {lines.map(l => (
            <tr key={l.id} className="border-b border-border/50 hover:bg-muted/20">
              <td className="px-3 py-2.5">
                <div className="font-medium">{l.partner_name}</div>
                <div className="text-muted-foreground text-[10px]">{l.partner_email}</div>
              </td>
              <td className="px-3 py-2.5 text-muted-foreground capitalize">{l.partner_type?.replace(/_/g, " ")}</td>
              <td className="px-3 py-2.5 text-center">{l.entry_count}</td>
              <td className="px-3 py-2.5">{fmt(l.total_gross)}</td>
              <td className="px-3 py-2.5 text-red-500">−{fmt(l.total_tds)}</td>
              <td className="px-3 py-2.5 font-semibold text-emerald-600">{fmt(l.total_net)}</td>
              <td className="px-3 py-2.5">
                {l.bank_verified
                  ? <span className="text-[9px] text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-1.5 py-0.5">Verified</span>
                  : <span className="text-[9px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-full px-1.5 py-0.5">Unverified</span>
                }
              </td>
              <td className="px-3 py-2.5">
                <span className={`text-[9px] uppercase tracking-[0.14em] border rounded-full px-2 py-0.5 font-semibold ${STATUS_STYLES[l.status] || ""}`}>{l.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── TDS Certificates Panel ──────────────────────────────────────────────────
const QUARTER_LABELS = { 1: "Apr–Jun", 2: "Jul–Sep", 3: "Oct–Dec", 4: "Jan–Mar" };
const LEVEL_LABELS = {
  sales_partner: "Sales Partner", sales_manager: "Sales Manager",
  zonal_partner: "Zonal", regional_partner: "Regional", national_partner: "National",
};

function TDSCertificatesTab() {
  const [partners, setPartners]     = useState([]);
  const [certs, setCerts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm]             = useState({ partner_id: "", financial_year: currentFY(), quarter: currentQ() });

  const loadCerts = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/sm/tds/partners-summary`, { withCredentials: true }),
      axios.get(`${API}/api/sm/tds`, { withCredentials: true }),
    ]).then(([pr, cr]) => {
      setPartners(pr.data.partners || []);
      setCerts(cr.data.certificates || []);
    }).catch(() => toast.error("Failed to load TDS data"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCerts(); }, [loadCerts]);

  const handleGenerate = async () => {
    if (!form.partner_id) { toast.error("Select a partner"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/sm/tds/generate`, form, { withCredentials: true });
      if (r.data.existing) {
        toast.info(`Certificate already exists: ${r.data.certificate_no}`);
      } else {
        toast.success(`Form 16A generated: ${r.data.certificate_no} · TDS ₹${Number(r.data.total_tds || 0).toLocaleString("en-IN")}`);
      }
      loadCerts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const handleDownload = (certId, certNo) => {
    const a = document.createElement("a");
    a.href = `${API}/api/sm/tds/${certId}/download`;
    a.download = `Form16A_${certNo?.replace(/\//g, "-")}.pdf`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fyOptions = (() => {
    const y = new Date().getFullYear();
    return [`${y}-${String(y+1).slice(-2)}`, `${y-1}-${String(y).slice(-2)}`, `${y-2}-${String(y-1).slice(-2)}`];
  })();

  const inputCls = "bg-background border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring transition-colors";

  return (
    <div className="space-y-5" data-testid="tds-certs-panel">
      {/* Generator */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Award className="w-4 h-4 text-primary" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Generate Form 16A Certificate</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Partner</label>
            <div className="relative">
              <select value={form.partner_id} onChange={e => setForm(f => ({ ...f, partner_id: e.target.value }))}
                className={`${inputCls} w-full pr-8 appearance-none`} data-testid="tds-partner-select">
                <option value="">-- Select Partner --</option>
                {partners.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({LEVEL_LABELS[p.partner_type] || p.partner_type})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Financial Year</label>
            <select value={form.financial_year} onChange={e => setForm(f => ({ ...f, financial_year: e.target.value }))}
              className={`${inputCls} w-28`} data-testid="tds-fy-select">
              {fyOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Quarter</label>
            <select value={form.quarter} onChange={e => setForm(f => ({ ...f, quarter: parseInt(e.target.value) }))}
              className={`${inputCls} w-36`} data-testid="tds-quarter-select">
              {[1,2,3,4].map(q => <option key={q} value={q}>Q{q} — {QUARTER_LABELS[q]}</option>)}
            </select>
          </div>
          <button onClick={handleGenerate} disabled={generating || !form.partner_id}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all"
            data-testid="generate-tds-btn">
            <FileText className="w-3.5 h-3.5" />
            {generating ? "Generating…" : "Generate Certificate"}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Aggregates all <strong>approved/paid</strong> commission ledger entries for the selected partner and quarter into a government-format Form 16A (Section 194H).
        </p>
      </div>

      {/* Certificates table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Generated Certificates</p>
          <button onClick={loadCerts} className="p-1.5 hover:bg-muted rounded-lg transition-all">
            <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[700px]">
          <thead className="border-b border-border bg-muted/20">
            <tr>{["Certificate No.", "Partner", "FY", "Quarter", "Gross Commission", "TDS Deducted", "Net Paid", "Action"].map(h => (
              <th key={h} className="px-5 py-3 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>}
            {!loading && certs.map(c => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-muted/20">
                <td className="px-5 py-3.5 font-mono text-[12px] text-primary">{c.certificate_no}</td>
                <td className="px-5 py-3.5 font-medium">{c.partner_name}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{c.financial_year}</td>
                <td className="px-5 py-3.5 text-muted-foreground">Q{c.quarter} ({QUARTER_LABELS[c.quarter]})</td>
                <td className="px-5 py-3.5">{fmt(c.total_gross)}</td>
                <td className="px-5 py-3.5 text-red-500 font-medium">−{fmt(c.total_tds)}</td>
                <td className="px-5 py-3.5 text-emerald-600 font-semibold">{fmt(c.total_net)}</td>
                <td className="px-5 py-3.5">
                  <button onClick={() => handleDownload(c.id, c.certificate_no)} data-testid={`download-tds-${c.id}`}
                    className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 border border-primary/30 text-primary rounded-lg hover:bg-primary/10 transition-all">
                    <Download className="w-3 h-3" />PDF
                  </button>
                </td>
              </tr>
            ))}
            {!loading && certs.length === 0 && (
              <tr><td colSpan={8} className="py-12 text-center text-muted-foreground text-sm">
                No TDS certificates generated yet. Select a partner above and click Generate.
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function SMStatements() {
  const [activeTab, setActiveTab]   = useState("statements");
  const [statements, setStatements] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeStmt, setActiveStmt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/sm/statements`, { withCredentials: true })
      .then(r => setStatements(r.data.statements || []))
      .catch(() => toast.error("Failed to load statements"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/sm/statements/generate`, {}, { withCredentials: true });
      if (r.data.skipped) toast.info(r.data.reason || "Already generated for this period");
      else toast.success(`Statement generated: ${r.data.period} · ${r.data.partners} partners · ${fmt(r.data.total_net)} net`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/api/sm/statements/${id}/approve`, {}, { withCredentials: true });
      toast.success("Statement approved"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
  };

  const handlePayout = async (id) => {
    if (!window.confirm("Initiate Razorpay X payouts for all partners in this statement? This will debit your Razorpay X account.")) return;
    try {
      await axios.post(`${API}/api/sm/statements/${id}/payout`, {}, { withCredentials: true });
      toast.success("Payout processing started"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Payout failed"); }
  };

  const TABS = [
    { key: "statements", label: "Monthly Statements", icon: FileText },
    { key: "tds",        label: "TDS Certificates (Form 16A)", icon: Award },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-statements">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Statements & TDS</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Monthly payouts · Form 16A certificates (Sec. 194H)</p>
        </div>
        {activeTab === "statements" && (
          <div className="flex gap-2">
            <button onClick={load} className="p-2 border border-border rounded-xl text-muted-foreground hover:bg-muted transition-all">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={handleGenerate} disabled={generating} data-testid="generate-stmt-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
              <Plus className="w-4 h-4" />{generating ? "Generating…" : "Generate This Month"}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/40 border border-border rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
              activeTab === t.key
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {activeTab === "tds" ? <TDSCertificatesTab /> : (
        <>
          {/* Info box */}
          <div className="flex items-start gap-3 bg-primary/5 border border-primary/15 rounded-xl p-4 text-[12px]">
            <AlertCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="text-muted-foreground">
              Statements aggregate all <strong className="text-foreground">approved</strong> commission entries for the month.
              Razorpay X payout credentials can be configured in <strong className="text-foreground">Settings → Razorpay X Configuration</strong>. Payouts are initiated per partner to their verified bank account.
            </div>
          </div>

          {activeStmt ? (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <StatementLines stmtId={activeStmt} onClose={() => setActiveStmt(null)} />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[680px]">
                <thead className="border-b border-border bg-muted/20">
                  <tr>{["Period", "Partners", "Gross Commission", "TDS", "Net Payable", "Status", "Actions"].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>}
                  {!loading && statements.map(s => (
                    <tr key={s.id} className="border-b border-border/50 hover:bg-muted/20 cursor-pointer group" onClick={() => setActiveStmt(s.id)}>
                      <td className="px-5 py-4 font-semibold text-foreground">{s.period_label}</td>
                      <td className="px-5 py-4 text-muted-foreground">{s.partner_count}</td>
                      <td className="px-5 py-4">{fmt(s.total_gross)}</td>
                      <td className="px-5 py-4 text-red-500">−{fmt(s.total_tds)}</td>
                      <td className="px-5 py-4 font-semibold text-emerald-600">{fmt(s.total_net)}</td>
                      <td className="px-5 py-4">
                        <span className={`text-[9px] uppercase tracking-[0.16em] border rounded-full px-2 py-0.5 font-semibold ${STATUS_STYLES[s.status] || ""}`}>{s.status}</span>
                      </td>
                      <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-2">
                          {s.status === "draft" && (
                            <button onClick={() => handleApprove(s.id)} data-testid={`approve-stmt-${s.id}`}
                              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-emerald-500/30 text-emerald-600 rounded-lg hover:bg-emerald-500/10 transition-all">
                              <CheckCircle2 className="w-3 h-3" />Approve
                            </button>
                          )}
                          {s.status === "approved" && (
                            <button onClick={() => handlePayout(s.id)} data-testid={`payout-stmt-${s.id}`}
                              className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-primary/30 text-primary rounded-lg hover:bg-primary/10 transition-all">
                              <Send className="w-3 h-3" />Pay Out
                            </button>
                          )}
                          {s.status === "paid" && <span className="text-emerald-600 text-[11px]">Paid</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && statements.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-muted-foreground text-sm">
                      No statements yet. Generate one after approving commission entries.
                    </td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
